import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Users, Shield, Key, Search } from 'lucide-react';

interface AccountGroup {
  id: string;
  name: string;
  role: string;
}

interface Account {
  id: string;
  email: string;
  status: 'active' | 'locked' | 'disabled';
  totpEnabled: boolean;
  roleName: string;
  hasE2eeKeys: boolean;
  groups: AccountGroup[];
  createdAt: string;
  lastLoginAt: string | null;
}

export function AccountsPage() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api
      .get<{ accounts: Account[] }>('/groups/accounts')
      .then((r) => setAccounts(r.accounts))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = accounts.filter(
    (a) =>
      a.email.toLowerCase().includes(search.toLowerCase()) ||
      a.roleName.toLowerCase().includes(search.toLowerCase()),
  );

  const statusBadge = (s: string) => {
    const map: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      locked: 'bg-yellow-100 text-yellow-700',
      disabled: 'bg-red-100 text-red-700',
    };
    const labels: Record<string, string> = {
      active: 'Aktiv',
      locked: 'Gesperrt',
      disabled: 'Deaktiviert',
    };
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[s] ?? 'bg-slate-100 text-slate-600'}`}>
        {labels[s] ?? s}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Accounts</h1>
          <p className="text-sm text-slate-500 mt-1">
            Alle Dashboard-Benutzerkonten im Überblick
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Users size={16} />
          <span>{accounts.length} Konten</span>
        </div>
      </div>

      {/* Suche */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Nach E-Mail oder Rolle suchen ..."
          className="input pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Laden ...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400">Keine Konten gefunden</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="pb-3 font-medium">E-Mail</th>
                <th className="pb-3 font-medium">Rolle</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">2FA</th>
                <th className="pb-3 font-medium">E2EE</th>
                <th className="pb-3 font-medium">Gruppen</th>
                <th className="pb-3 font-medium">Letzter Login</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="py-3 font-medium text-slate-900">{a.email}</td>
                  <td className="py-3">
                    <span className="inline-flex items-center gap-1">
                      <Shield size={14} className="text-rla-500" />
                      {a.roleName}
                    </span>
                  </td>
                  <td className="py-3">{statusBadge(a.status)}</td>
                  <td className="py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.totpEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {a.totpEnabled ? 'Aktiv' : 'Aus'}
                    </span>
                  </td>
                  <td className="py-3">
                    <span className="inline-flex items-center gap-1">
                      <Key size={14} className={a.hasE2eeKeys ? 'text-green-600' : 'text-slate-300'} />
                      <span className={`text-xs ${a.hasE2eeKeys ? 'text-green-600' : 'text-slate-400'}`}>
                        {a.hasE2eeKeys ? 'Bereit' : 'Fehlt'}
                      </span>
                    </span>
                  </td>
                  <td className="py-3">
                    {a.groups.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {a.groups.map((g) => (
                          <span
                            key={g.id}
                            className="text-xs bg-rla-50 text-rla-700 px-2 py-0.5 rounded-full"
                          >
                            {g.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Keine</span>
                    )}
                  </td>
                  <td className="py-3 text-slate-500 text-xs">
                    {a.lastLoginAt
                      ? new Date(a.lastLoginAt).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Nie'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
